// Encyclopedie (was Compendium): overzicht + detail met live Wikipedia.
// Overzicht heeft een sort-toggle (alfabetisch ↔ thematisch).
// Detail toont samenvatting + uitklap voor het volledige Wikipedia-artikel.

import { setTitle } from '../main.js'
import { md } from '../markdown.js'
import { icon } from '../icons.js'
import { getStore, setWikipediaEntry } from '../store.js'
import { getWikipedia, getWikipediaFull, getWikipediaLanglinks } from '../api.js'
import { esc } from '../util.js'

const LANG_LABELS = { en: 'EN', nl: 'NL', cs: 'CS', de: 'DE' }
const langlinksCache = new Map()

function renderImageCredit(obj) {
  const img = obj?.imageRef
  if (!img) return ''
  const parts = []
  if (img.credit) parts.push(esc(img.credit))
  if (img.license) {
    parts.push(img.licenseUrl
      ? `<a href="${esc(img.licenseUrl)}" target="_blank" rel="noopener">${esc(img.license)}</a>`
      : esc(img.license))
  }
  if (img.source) {
    parts.push(`<a href="${esc(img.source)}" target="_blank" rel="noopener">bron</a>`)
  }
  return parts.length
    ? `<div class="image-credit">${parts.join(' · ')}</div>`
    : ''
}

const KIND_LABELS = {
  geschiedenis: 'Geschiedenis',
  regio:        'Regio',
  persoon:      'Persoon',
  gerecht:      'Gerecht',
  cultuur:      'Cultuur',
  overig:       'Overig',
}

// Sort-state leeft buiten de render, vasthoudend tussen tabswitches
let sortMode = 'thematisch'  // 'thematisch' | 'alfabetisch'

function renderItemRow(c) {
  return `
    <a class="entity" href="#/encyclopedie/${c.id}">
      <div class="icon-bubble">${icon('book-open')}</div>
      <div class="e-body">
        <div class="e-title">${esc(c.title)}</div>
        ${c.summary ? `<div class="e-note">${esc(c.summary.slice(0, 160))}${c.summary.length > 160 ? '…' : ''}</div>` : ''}
        ${c.wikipedia ? `<div class="e-meta">incl. Wikipedia</div>` : ''}
      </div>
    </a>`
}

export function renderEncyclopedia(root, ctx) {
  setTitle('')
  const items = ctx.data.compendium ?? []

  let body
  if (!items.length) {
    body = '<p class="empty">Nog geen entries.</p>'
  } else if (sortMode === 'alfabetisch') {
    const sorted = [...items].sort((a, b) => a.title.localeCompare(b.title, 'nl'))
    body = `<div class="entity-list">${sorted.map(renderItemRow).join('')}</div>`
  } else {
    const groups = new Map()
    for (const k of Object.keys(KIND_LABELS)) groups.set(k, [])
    for (const c of items) {
      if (!groups.has(c.kind)) groups.set(c.kind, [])
      groups.get(c.kind).push(c)
    }
    const blocks = []
    for (const [kind, list] of groups) {
      if (!list.length) continue
      const sorted = [...list].sort((a, b) => a.title.localeCompare(b.title, 'nl'))
      blocks.push(`
        <details class="enc-group">
          <summary class="section-heading">${esc(KIND_LABELS[kind])} <span class="enc-count">(${list.length})</span></summary>
          <div class="entity-list">${sorted.map(renderItemRow).join('')}</div>
        </details>
      `)
    }
    body = blocks.join('')
  }

  root.innerHTML = `
    <div class="page">
      <h1 class="title">Encyclopedie</h1>
      <p class="subtitle">Verhalen achter de plekken: geschiedenis, mensen, gerechten en regio's.</p>
      <div class="sort-toggle" role="group" aria-label="Sorteer">
        <button class="chip toggle ${sortMode === 'thematisch' ? 'active' : ''}" data-sort="thematisch">Thematisch</button>
        <button class="chip toggle ${sortMode === 'alfabetisch' ? 'active' : ''}" data-sort="alfabetisch">A → Z</button>
      </div>
      ${body}
    </div>
  `

  root.querySelectorAll('.sort-toggle [data-sort]').forEach(b => {
    b.addEventListener('click', () => {
      sortMode = b.dataset.sort
      renderEncyclopedia(root, ctx)
    })
  })
}

// ---------- Detail ----------

function wikiSafeId(cacheKey) {
  return cacheKey.replace(/[^a-z0-9-]/gi, '_')
}

function renderRelated(c, ctx) {
  const out = []
  const nodes = (c.nodeIds ?? []).map(id => ctx.indexes.nodesById.get(id)).filter(Boolean)
  if (nodes.length) {
    out.push(`
      <div class="card">
        <h2>Gekoppelde plekken</h2>
        <div class="entity-list">
          ${nodes.map(n => `
            <a class="entity" href="#/node/${n.id}">
              <div class="icon-bubble">${icon('map')}</div>
              <div class="e-body">
                <div class="e-title">${esc(n.name)}</div>
                ${n.blurb ? `<div class="e-note">${esc(n.blurb).slice(0, 160)}</div>` : ''}
              </div>
            </a>`).join('')}
        </div>
      </div>`)
  }
  const people = (c.personIds ?? []).map(id => ctx.indexes.peopleById.get(id)).filter(Boolean)
  if (people.length) {
    out.push(`
      <div class="card">
        <h2>Gekoppelde figuren</h2>
        <div class="entity-list">
          ${people.map(p => `
            <a class="entity" href="#/people/${p.id}">
              <div class="icon-bubble">${icon('users')}</div>
              <div class="e-body">
                <div class="e-title">${esc(p.name)}</div>
                ${p.years ? `<div class="e-meta">${esc(p.years)}</div>` : ''}
              </div>
            </a>`).join('')}
        </div>
      </div>`)
  }
  const related = (c.relatedIds ?? []).map(id => ctx.indexes.compendiumById.get(id)).filter(Boolean)
  if (related.length) {
    out.push(`
      <div class="card">
        <h2>Verwante verhalen</h2>
        <div class="entity-list">
          ${related.map(r => `
            <a class="entity" href="#/encyclopedie/${r.id}">
              <div class="icon-bubble">${icon('book-open')}</div>
              <div class="e-body">
                <div class="e-title">${esc(r.title)}</div>
              </div>
            </a>`).join('')}
        </div>
      </div>`)
  }
  return out.join('')
}

function renderWikipediaBlock(c) {
  if (!c.wikipedia) return ''
  const w = c.wikipedia
  return `
    <div class="wiki-block" id="wiki-block-${esc(c.id)}">
      ${renderWikiContent(w)}
    </div>`
}

function renderWikiContent(w) {
  const cacheKey = `${w.title}@${w.lang}`
  const cached = getStore().wikipediaCache[cacheKey]
  const safeId = wikiSafeId(cacheKey)
  const wikiUrl = w.url || `https://${w.lang}.wikipedia.org/wiki/${encodeURIComponent(w.title)}`

  const summaryHtml = cached
    ? `<p>${esc(cached.summary)}</p>`
    : `<p class="empty">Samenvatting wordt geladen…</p>`

  return `
    <h2>Wikipedia: ${esc(w.title)}</h2>
    <div class="wiki-lang-toggle" id="wiki-lang-toggle"></div>
    <div class="section-body" id="wiki-body-${safeId}">${summaryHtml}</div>
    <details class="wiki-full" data-title="${esc(w.title)}" data-lang="${esc(w.lang)}">
      <summary>${icon('book-open')} Toon hele Wikipedia-artikel</summary>
      <div class="wiki-full-body" id="wiki-full-${safeId}">
        <p class="empty">Artikel wordt geladen…</p>
      </div>
    </details>
    <div class="wiki-attribution">
      Tekst van <a href="${esc(wikiUrl)}" target="_blank" rel="noopener">Wikipedia</a> ·
      licentie <a href="${esc(w.licenseUrl || 'https://creativecommons.org/licenses/by-sa/4.0/')}" target="_blank" rel="noopener">${esc(w.license || 'CC BY-SA 4.0')}</a>
      ${cached?.fetchedAt ? ` · opgehaald ${new Date(cached.fetchedAt).toLocaleDateString('nl-NL')}` : ''}
    </div>`
}

async function loadWikipediaInto(root, w) {
  const cacheKey = `${w.title}@${w.lang}`
  const safeId = wikiSafeId(cacheKey)
  const body = root.querySelector(`#wiki-body-${safeId}`)
  try {
    const res = await getWikipedia(w.title, w.lang)
    setWikipediaEntry(cacheKey, res)
    if (body) body.innerHTML = `<p>${esc(res.summary)}</p>`
  } catch (e) {
    if (body) body.innerHTML = `<p class="empty">Wikipedia-samenvatting niet beschikbaar (${esc(e.message)}).</p>`
  }
}

async function discoverLangs(w) {
  const key = `${w.title}@${w.lang}`
  if (langlinksCache.has(key)) return langlinksCache.get(key)

  const editions = [{ lang: w.lang, title: w.title }]
  const targets = ['en', 'nl'].filter(l => l !== w.lang)
  const results = await Promise.allSettled(
    targets.map(t => getWikipediaLanglinks(w.title, w.lang, t))
  )
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value?.result) {
      editions.push({ lang: r.value.result.lang, title: r.value.result.title })
    }
  }
  langlinksCache.set(key, editions)
  return editions
}

function renderLangButtons(editions, activeLang) {
  if (editions.length < 2) return ''
  return `<div class="sort-toggle" role="group" aria-label="Taal">
    ${editions.map(e =>
      `<button class="chip toggle ${e.lang === activeLang ? 'active' : ''}" data-wiki-lang="${esc(e.lang)}" data-wiki-title="${esc(e.title)}">${LANG_LABELS[e.lang] || e.lang.toUpperCase()}</button>`
    ).join('')}
  </div>`
}

function wireLangToggle(root, c, editions) {
  const block = root.querySelector(`#wiki-block-${CSS.escape(c.id)}`)
  if (!block) return

  const slot = block.querySelector('#wiki-lang-toggle')
  if (!slot || editions.length < 2) return
  slot.innerHTML = renderLangButtons(editions, c.wikipedia.lang)

  slot.querySelectorAll('[data-wiki-lang]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const lang = btn.dataset.wikiLang
      const title = btn.dataset.wikiTitle
      const w = {
        title, lang,
        url: `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title)}`,
        license: c.wikipedia.license || 'CC BY-SA 4.0',
        licenseUrl: c.wikipedia.licenseUrl || 'https://creativecommons.org/licenses/by-sa/4.0/',
        mode: c.wikipedia.mode,
      }
      block.innerHTML = renderWikiContent(w)

      const newSlot = block.querySelector('#wiki-lang-toggle')
      if (newSlot) {
        newSlot.innerHTML = renderLangButtons(editions, lang)
        wireLangToggle(root, { ...c, wikipedia: w }, editions)
      }

      const cacheKey = `${title}@${lang}`
      if (!getStore().wikipediaCache[cacheKey]) await loadWikipediaInto(root, w)
      wireFullExpander(root)
    })
  })
}

function wireFullExpander(root) {
  root.querySelectorAll('details.wiki-full').forEach(det => {
    det.addEventListener('toggle', async () => {
      if (!det.open || det.dataset.loaded === '1') return
      const title = det.dataset.title
      const lang = det.dataset.lang
      const slot = det.querySelector('.wiki-full-body')
      det.dataset.loaded = '1'
      try {
        const res = await getWikipediaFull(title, lang)
        slot.innerHTML = `<div class="wikipedia-content">${res.html ?? ''}</div>`
        slot.querySelectorAll('a').forEach(a => {
          const href = a.getAttribute('href') || ''
          if (href.startsWith('//')) a.setAttribute('href', 'https:' + href)
          else if (href.startsWith('/wiki/')) a.setAttribute('href', `https://${lang}.wikipedia.org${href}`)
          a.setAttribute('target', '_blank')
          a.setAttribute('rel', 'noopener')
        })
      } catch (e) {
        slot.innerHTML = `<p class="empty">Volledig artikel niet bereikbaar (${esc(e.message)}).</p>`
        det.dataset.loaded = ''
      }
    })
  })
}

export function renderEncyclopediaItem(root, ctx, id) {
  const c = ctx.indexes.compendiumById.get(id)
  if (!c) {
    setTitle('')
    root.innerHTML = `<div class="page"><p class="empty">Onbekend: ${esc(id)}</p></div>`
    return
  }
  setTitle(c.title)

  const kindLabel = KIND_LABELS[c.kind] ?? c.kind
  const eras = (c.eras ?? []).map(eid => {
    const era = ctx.indexes.eraById.get(eid)
    return era ? `<span class="badge era" style="background:${era.color}">${esc(era.label)}</span>` : ''
  }).join('')

  const sections = (c.sections ?? []).map(s => {
    const body = `<div class="section-body">${md(s.body)}</div>`
    if (s.depth === 'deep') {
      return `<details class="deep"><summary>${esc(s.title ?? s.type)}</summary>${body}</details>`
    }
    return `<div class="card"><h2>${esc(s.title ?? s.type)}</h2>${body}</div>`
  }).join('')

  const heroImage = c.imageRef?.thumbUrl || c.imageRef?.url
  const imageCredit = renderImageCredit(c)

  root.innerHTML = `
    <div class="page">
      <div class="crumbs"><a href="#/encyclopedie">‹ Encyclopedie</a></div>
      ${heroImage ? `<img class="hero-img" src="${esc(heroImage)}" alt="" loading="lazy"/>` : ''}
      ${imageCredit}
      <h1 class="title">${esc(c.title)}</h1>
      <p class="subtitle">${esc(kindLabel)}</p>
      ${eras ? `<div class="badge-row">${eras}</div>` : ''}
      ${c.summary ? `<div class="blurb">${md(c.summary)}</div>` : ''}
      ${sections}
      ${renderWikipediaBlock(c)}
      ${renderRelated(c, ctx)}
      ${c.links?.length ? `
        <div class="card">
          <h2>Links</h2>
          <ul class="link-list">
            ${c.links.map(l => `<li><a href="${esc(l.url)}" target="_blank" rel="noopener">${icon('external-link')} ${esc(l.label)}</a></li>`).join('')}
          </ul>
        </div>` : ''}
    </div>
  `

  if (c.wikipedia && c.wikipedia.mode === 'live') {
    const cacheKey = `${c.wikipedia.title}@${c.wikipedia.lang}`
    if (!getStore().wikipediaCache[cacheKey]) loadWikipediaInto(root, c.wikipedia)
    wireFullExpander(root)
    discoverLangs(c.wikipedia).then(editions => wireLangToggle(root, c, editions))
  }
}
